import { useState, useEffect } from 'react'
import { Upload, Search, Filter, Trash2, File, ExternalLink } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import ArtifactUpload from '../components/ArtifactUpload'

export default function Artifacts() {
  const [artifacts, setArtifacts] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [loading, setLoading] = useState(true)
  const [deleteLoading, setDeleteLoading] = useState(null)
  const { isAuthenticated, isAdmin, user, getAuthToken } = useAuth()

  useEffect(() => {
    loadArtifacts()
  }, [])

  const loadArtifacts = async () => {
    try {
      setLoading(true)
      const response = await fetch(`${import.meta.env.VITE_API_ENDPOINT}/artifacts`)
      const data = await response.json()
      if (data.success) {
        setArtifacts(data.data.artifacts)
      }
    } catch (error) {
      console.error('Error loading artifacts:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleUploadSuccess = (newArtifact) => {
    setArtifacts(prev => [newArtifact, ...prev])
  }

  const handleDeleteArtifact = async (artifactId, artifactTitle) => {
    if (!confirm(`Are you sure you want to delete "${artifactTitle}"? This action cannot be undone.`)) {
      return
    }

    try {
      setDeleteLoading(artifactId)
      const token = await getAuthToken()
      
      const response = await fetch(`${import.meta.env.VITE_API_ENDPOINT}/artifacts/${artifactId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      })

      if (response.ok) {
        setArtifacts(prev => prev.filter(artifact => artifact.id !== artifactId))
        alert('Artifact deleted successfully')
      } else {
        const error = await response.json()
        alert(`Error: ${error.message}`)
      }
    } catch (error) {
      console.error('Error deleting artifact:', error)
      alert('Failed to delete artifact')
    } finally {
      setDeleteLoading(null)
    }
  }

  const canDeleteArtifact = (artifact) => {
    return isAdmin || (isAuthenticated && artifact.uploadedByEmail === user?.email)
  }

  const filteredArtifacts = artifacts.filter(artifact =>
    artifact.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    artifact.description.toLowerCase().includes(searchTerm.toLowerCase())
  )

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="content-container p-8 mb-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Cultural Artifacts
            </h1>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              Explore and preserve the rich cultural heritage of the Kapcheboin clan
            </p>
          </div>
        </div>

        {/* Search and Upload */}
        <div className="content-container p-6 mb-8">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search artifacts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field pl-10"
              />
            </div>
            {isAuthenticated && (
              <button
                onClick={() => setShowUploadModal(true)}
                className="btn-primary flex items-center space-x-2"
              >
                <Upload className="w-5 h-5" />
                <span>Upload Artifact</span>
              </button>
            )}
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="content-container p-12">
            <div className="flex justify-center items-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
          </div>
        )}

        {/* Artifacts Grid */}
        {!loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredArtifacts.map((artifact) => {
              const isPDF = artifact.metadata?.contentType === 'pdf' || artifact.metadata?.fileType === 'application/pdf'
              
              return (
                <div key={artifact.id} className="card-glass hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                  <div className="aspect-w-16 aspect-h-9 mb-4">
                    {isPDF ? (
                      <div className="w-full h-48 bg-gray-100 rounded-lg shadow-md flex flex-col items-center justify-center space-y-2">
                        <File className="w-16 h-16 text-red-500" />
                        <span className="text-sm font-medium text-gray-700">PDF Document</span>
                        <a
                          href={artifact.imageUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center space-x-1 text-primary-600 hover:text-primary-700 text-sm"
                        >
                          <ExternalLink className="w-4 h-4" />
                          <span>View PDF</span>
                        </a>
                      </div>
                    ) : (
                      <img
                        src={artifact.imageUrl || '/api/placeholder/300/200'}
                        alt={artifact.title}
                        className="w-full h-48 object-cover rounded-lg shadow-md"
                        onError={(e) => {
                          e.target.src = '/api/placeholder/300/200'
                        }}
                      />
                    )}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {artifact.title}
                  </h3>
                  <p className="text-gray-700 mb-4 leading-relaxed">
                    {artifact.description}
                  </p>
                  <div className="flex justify-between items-center text-sm text-gray-600 mb-3">
                    <span>By {artifact.uploadedBy}</span>
                    <div className="flex items-center space-x-2">
                      <span>{new Date(artifact.uploadDate).toLocaleDateString()}</span>
                      {canDeleteArtifact(artifact) && (
                        <button
                          onClick={() => handleDeleteArtifact(artifact.id, artifact.title)}
                          disabled={deleteLoading === artifact.id}
                          className="text-red-600 hover:text-red-800 disabled:opacity-50 p-1"
                          title="Delete artifact"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <span className="inline-block bg-primary-100 text-primary-800 text-xs px-3 py-1 rounded-full font-medium">
                      {artifact.category}
                    </span>
                    {isPDF && (
                      <span className="inline-block bg-red-100 text-red-800 text-xs px-2 py-1 rounded-full font-medium">
                        PDF
                      </span>
                    )}
                    {artifact.tags && artifact.tags.map((tag, index) => (
                      <span key={index} className="inline-block bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded-full">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {/* Empty State */}
        {!loading && filteredArtifacts.length === 0 && (
          <div className="content-container p-12">
            <div className="text-center">
              <div className="w-24 h-24 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                <Upload className="w-12 h-12 text-white" />
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                {searchTerm ? 'No artifacts found' : 'No artifacts yet'}
              </h3>
              <p className="text-gray-600 mb-8 max-w-md mx-auto">
                {searchTerm 
                  ? 'Try adjusting your search terms to find what you\'re looking for.'
                  : 'Be the first to preserve our cultural heritage by uploading an artifact.'
                }
              </p>
              {isAuthenticated && !searchTerm && (
                <button
                  onClick={() => setShowUploadModal(true)}
                  className="btn-primary"
                >
                  Upload First Artifact
                </button>
              )}
              {!isAuthenticated && (
                <p className="text-sm text-gray-500">
                  <a href="/login" className="text-primary-600 hover:text-primary-700 font-medium">Sign in</a> to upload artifacts
                </p>
              )}
            </div>
          </div>
        )}

        {/* Upload Modal */}
        <ArtifactUpload
          isOpen={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          onSuccess={handleUploadSuccess}
        />
      </div>
    </div>
  )
}